import { useState, useEffect, useRef } from "react";
import {
  Save,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Upload,
  Image,
} from "lucide-react";
import toast from "react-hot-toast";
import { contentService } from "../../services/contentService";
import { uploadImage } from "../../services/uploadService";
import { imgUrl } from "../../utils/imgUrl";

// ── Default content (used when DB has no entry yet) ──────────────────────────
const DEFAULTS = {
  hero: {
    bg_image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop",
    title: "Burans Hikes Home Stay",
    subtitle:
      "Experience the serenity of Dhanaulti amidst the Himalayan beauty",
  },
  about: {
    image:
      "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&h=600&fit=crop",
    heading: "About Us",
    text1:
      "Nestled in the picturesque hill station of Dhanaulti, Burans Hikes Home Stay offers a perfect blend of comfort and natural beauty. Our homestay is your gateway to experiencing the majestic Himalayas.",
    text2:
      "Whether you're seeking adventure or tranquility, our warm hospitality and stunning surroundings make for an unforgettable mountain retreat.",
  },
  stats: [
    { number: "2280m", label: "Altitude", color: "#16a34a" },
    { number: "500+", label: "Happy Guests", color: "#2563eb" },
    { number: "4.6/5", label: "Average Rating", color: "#f59e0b" },
    { number: "2+", label: "Years Experience", color: "#8b5cf6" },
  ],
  features: [
    {
      emoji: "🏔️",
      title: "Scenic Views",
      description: "Breathtaking panoramic views of the Himalayas",
    },
    {
      emoji: "🍽️",
      title: "Local Cuisine",
      description: "Authentic Garhwali delicacies prepared with love",
    },
    {
      emoji: "🥾",
      title: "Trekking Routes",
      description: "Easy access to beautiful hiking trails",
    },
    {
      emoji: "🌲",
      title: "Nature Walks",
      description: "Explore pristine deodar and oak forests",
    },
    {
      emoji: "🔥",
      title: "Bonfire Nights",
      description: "Cozy evenings under the starlit sky",
    },
    {
      emoji: "📶",
      title: "Modern Amenities",
      description: "WiFi, hot water, and comfortable rooms",
    },
  ],
  testimonials: [
    {
      name: "Rahul Sharma",
      location: "Delhi",
      rating: 5,
      text: "Amazing experience! The views were breathtaking and the hospitality was excellent. Perfect place for a peaceful getaway.",
    },
    {
      name: "Priya Patel",
      location: "Mumbai",
      rating: 5,
      text: "Loved every moment of our stay. The rooms were clean, food was delicious, and the staff was very friendly.",
    },
    {
      name: "Amit Kumar",
      location: "Bangalore",
      rating: 5,
      text: "Best homestay in Dhanaulti! Perfect for families. Kids enjoyed the bonfire and nature walks.",
    },
  ],
};

const Section = ({ title, open, onToggle, children }) => (
  <div style={s.section}>
    <button style={s.sectionHeader} onClick={onToggle}>
      <span style={s.sectionTitle}>{title}</span>
      {open ? (
        <ChevronUp size={20} color="#6b7280" />
      ) : (
        <ChevronDown size={20} color="#6b7280" />
      )}
    </button>
    {open && <div style={s.sectionBody}>{children}</div>}
  </div>
);

const ManageSiteContent = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [uploading, setUploading] = useState({});
  const [open, setOpen] = useState({
    hero: true,
    about: false,
    stats: false,
    features: false,
    testimonials: false,
  });
  const heroImgRef = useRef(null);
  const aboutImgRef = useRef(null);

  const handleImageUpload = async (section, field, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [`${section}.${field}`]: true }));
    try {
      const url = await uploadImage(file);
      setField(section, field, url);
      toast.success("Image uploaded!");
    } catch {
      toast.error("Image upload failed.");
    } finally {
      setUploading((prev) => ({ ...prev, [`${section}.${field}`]: false }));
    }
  };

  useEffect(() => {
    contentService
      .getSiteContent()
      .then((res) => {
        const c = res.data.content || {};
        setContent({
          hero: c.hero || DEFAULTS.hero,
          about: c.about || DEFAULTS.about,
          stats: c.stats || DEFAULTS.stats,
          features: c.features || DEFAULTS.features,
          testimonials: c.testimonials || DEFAULTS.testimonials,
        });
      })
      .catch(() => {
        setContent({
          hero: DEFAULTS.hero,
          about: DEFAULTS.about,
          stats: DEFAULTS.stats,
          features: DEFAULTS.features,
          testimonials: DEFAULTS.testimonials,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const save = async (key) => {
    setSaving(key);
    try {
      await contentService.upsertSiteContent(key, content[key]);
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} saved!`);
    } catch {
      toast.error("Failed to save.");
    } finally {
      setSaving(null);
    }
  };

  const set = (key, val) => setContent((prev) => ({ ...prev, [key]: val }));
  const setField = (key, field, val) =>
    setContent((prev) => ({ ...prev, [key]: { ...prev[key], [field]: val } }));
  const setArrItem = (key, idx, field, val) => {
    const arr = [...(content[key] || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    set(key, arr);
  };
  const addArrItem = (key, item) => set(key, [...(content[key] || []), item]);
  const removeArrItem = (key, idx) =>
    set(
      key,
      content[key].filter((_, i) => i !== idx),
    );

  const SaveBtn = ({ k }) => (
    <button
      style={{ ...s.saveBtn, opacity: saving === k ? 0.7 : 1 }}
      onClick={() => save(k)}
      disabled={saving === k}
    >
      <Save size={16} /> {saving === k ? "Saving..." : "Save Section"}
    </button>
  );

  if (loading)
    return (
      <div style={s.container}>
        <p style={{ color: "#6b7280" }}>Loading content...</p>
      </div>
    );

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.pageTitle}>Site Content</h1>
        <p style={s.pageSubtitle}>
          Manage home page content — changes go live instantly
        </p>
      </div>

      {/* HERO */}
      <Section
        title="🏔️ Hero Section"
        open={open.hero}
        onToggle={() => toggle("hero")}
      >
        <div style={s.inputGroup}>
          <label style={s.label}>Background Image</label>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={heroImgRef}
            onChange={(e) =>
              handleImageUpload("hero", "bg_image", e.target.files[0])
            }
          />
          <button
            type="button"
            style={{
              ...s.filePickerBtn,
              ...(uploading["hero.bg_image"] ? { opacity: 0.6 } : {}),
            }}
            onClick={() => heroImgRef.current?.click()}
            disabled={uploading["hero.bg_image"]}
          >
            {uploading["hero.bg_image"] ? (
              <>
                <Upload size={15} /> Uploading...
              </>
            ) : (
              <>
                <Image size={15} />{" "}
                {content.hero?.bg_image ? "Change Image" : "Upload Image"}
              </>
            )}
          </button>
          <p style={s.sizeHint}>
            Recommended: <strong>1920 × 1080px</strong> (landscape) · JPG/PNG ·
            Max 5 MB · Used as full-screen hero background
          </p>
          {content.hero?.bg_image && (
            <div style={{ marginTop: 8, position: "relative" }}>
              <img
                src={imgUrl(content.hero.bg_image)}
                alt="hero preview"
                style={{
                  width: "100%",
                  height: "120px",
                  objectFit: "cover",
                  borderRadius: "10px",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <span
                style={{
                  position: "absolute",
                  bottom: 8,
                  left: 8,
                  background: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 6,
                }}
              >
                {content.hero.bg_image.startsWith("/uploads/")
                  ? content.hero.bg_image.split("/").pop()
                  : "External URL"}
              </span>
            </div>
          )}
        </div>
        <div style={s.inputGroup}>
          <label style={s.label}>Main Title</label>
          <input
            style={s.input}
            value={content.hero?.title || ""}
            onChange={(e) => setField("hero", "title", e.target.value)}
          />
        </div>
        <div style={s.inputGroup}>
          <label style={s.label}>Subtitle</label>
          <input
            style={s.input}
            value={content.hero?.subtitle || ""}
            onChange={(e) => setField("hero", "subtitle", e.target.value)}
          />
        </div>
        <SaveBtn k="hero" />
      </Section>

      {/* ABOUT */}
      <Section
        title="ℹ️ About Section"
        open={open.about}
        onToggle={() => toggle("about")}
      >
        <div style={s.inputGroup}>
          <label style={s.label}>Section Image</label>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={aboutImgRef}
            onChange={(e) =>
              handleImageUpload("about", "image", e.target.files[0])
            }
          />
          <button
            type="button"
            style={{
              ...s.filePickerBtn,
              ...(uploading["about.image"] ? { opacity: 0.6 } : {}),
            }}
            onClick={() => aboutImgRef.current?.click()}
            disabled={uploading["about.image"]}
          >
            {uploading["about.image"] ? (
              <>
                <Upload size={15} /> Uploading...
              </>
            ) : (
              <>
                <Image size={15} />{" "}
                {content.about?.image ? "Change Image" : "Upload Image"}
              </>
            )}
          </button>
          <p style={s.sizeHint}>
            Recommended: <strong>800 × 600px</strong> (portrait or square) ·
            JPG/PNG · Max 5 MB · Displayed beside the About Us text
          </p>
          {content.about?.image && (
            <div
              style={{
                marginTop: 8,
                position: "relative",
                display: "inline-block",
              }}
            >
              <img
                src={imgUrl(content.about.image)}
                alt="about preview"
                style={{
                  width: "200px",
                  height: "140px",
                  objectFit: "cover",
                  borderRadius: "10px",
                  display: "block",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <span
                style={{
                  position: "absolute",
                  bottom: 8,
                  left: 8,
                  background: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 6,
                }}
              >
                {content.about.image.startsWith("/uploads/")
                  ? content.about.image.split("/").pop()
                  : "External URL"}
              </span>
            </div>
          )}
        </div>
        <div style={s.inputGroup}>
          <label style={s.label}>Heading</label>
          <input
            style={s.input}
            value={content.about?.heading || ""}
            onChange={(e) => setField("about", "heading", e.target.value)}
          />
        </div>
        <div style={s.inputGroup}>
          <label style={s.label}>Paragraph 1</label>
          <textarea
            style={s.textarea}
            rows={3}
            value={content.about?.text1 || ""}
            onChange={(e) => setField("about", "text1", e.target.value)}
          />
        </div>
        <div style={s.inputGroup}>
          <label style={s.label}>Paragraph 2</label>
          <textarea
            style={s.textarea}
            rows={3}
            value={content.about?.text2 || ""}
            onChange={(e) => setField("about", "text2", e.target.value)}
          />
        </div>
        <SaveBtn k="about" />
      </Section>

      {/* STATS */}
      <Section
        title="📊 Stats Section"
        open={open.stats}
        onToggle={() => toggle("stats")}
      >
        {(content.stats || []).map((stat, i) => (
          <div key={i} style={s.arrayCard}>
            <div style={s.arrayCardHeader}>
              <span style={s.arrayCardTitle}>Stat #{i + 1}</span>
              <button
                style={s.removeBtn}
                onClick={() => removeArrItem("stats", i)}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div style={s.formRow}>
              <div style={{ ...s.inputGroup, flex: 1 }}>
                <label style={s.label}>Number / Value</label>
                <input
                  style={s.input}
                  value={stat.number}
                  placeholder="e.g. 500+"
                  onChange={(e) =>
                    setArrItem("stats", i, "number", e.target.value)
                  }
                />
              </div>
              <div style={{ ...s.inputGroup, flex: 1 }}>
                <label style={s.label}>Label</label>
                <input
                  style={s.input}
                  value={stat.label}
                  placeholder="e.g. Happy Guests"
                  onChange={(e) =>
                    setArrItem("stats", i, "label", e.target.value)
                  }
                />
              </div>
              <div style={{ ...s.inputGroup, flex: 0.6 }}>
                <label style={s.label}>Color</label>
                <input
                  style={{ ...s.input, padding: "6px" }}
                  type="color"
                  value={stat.color}
                  onChange={(e) =>
                    setArrItem("stats", i, "color", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        ))}
        <button
          style={s.addItemBtn}
          onClick={() =>
            addArrItem("stats", { number: "", label: "", color: "#16a34a" })
          }
        >
          <Plus size={16} /> Add Stat
        </button>
        <SaveBtn k="stats" />
      </Section>

      {/* FEATURES */}
      <Section
        title="✨ Features / Why Choose Us"
        open={open.features}
        onToggle={() => toggle("features")}
      >
        {(content.features || []).map((feat, i) => (
          <div key={i} style={s.arrayCard}>
            <div style={s.arrayCardHeader}>
              <span style={s.arrayCardTitle}>Feature #{i + 1}</span>
              <button
                style={s.removeBtn}
                onClick={() => removeArrItem("features", i)}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div style={s.formRow}>
              <div style={{ ...s.inputGroup, flex: 0.5 }}>
                <label style={s.label}>Emoji</label>
                <input
                  style={s.input}
                  value={feat.emoji}
                  placeholder="🏔️"
                  onChange={(e) =>
                    setArrItem("features", i, "emoji", e.target.value)
                  }
                />
              </div>
              <div style={{ ...s.inputGroup, flex: 1 }}>
                <label style={s.label}>Title</label>
                <input
                  style={s.input}
                  value={feat.title}
                  onChange={(e) =>
                    setArrItem("features", i, "title", e.target.value)
                  }
                />
              </div>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Description</label>
              <input
                style={s.input}
                value={feat.description}
                onChange={(e) =>
                  setArrItem("features", i, "description", e.target.value)
                }
              />
            </div>
          </div>
        ))}
        <button
          style={s.addItemBtn}
          onClick={() =>
            addArrItem("features", { emoji: "", title: "", description: "" })
          }
        >
          <Plus size={16} /> Add Feature
        </button>
        <SaveBtn k="features" />
      </Section>

      {/* TESTIMONIALS */}
      <Section
        title="💬 Testimonials"
        open={open.testimonials}
        onToggle={() => toggle("testimonials")}
      >
        {(content.testimonials || []).map((t, i) => (
          <div key={i} style={s.arrayCard}>
            <div style={s.arrayCardHeader}>
              <span style={s.arrayCardTitle}>Testimonial #{i + 1}</span>
              <button
                style={s.removeBtn}
                onClick={() => removeArrItem("testimonials", i)}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div style={s.formRow}>
              <div style={{ ...s.inputGroup, flex: 1 }}>
                <label style={s.label}>Name</label>
                <input
                  style={s.input}
                  value={t.name}
                  onChange={(e) =>
                    setArrItem("testimonials", i, "name", e.target.value)
                  }
                />
              </div>
              <div style={{ ...s.inputGroup, flex: 1 }}>
                <label style={s.label}>Location</label>
                <input
                  style={s.input}
                  value={t.location}
                  onChange={(e) =>
                    setArrItem("testimonials", i, "location", e.target.value)
                  }
                />
              </div>
              <div style={{ ...s.inputGroup, flex: 0.5 }}>
                <label style={s.label}>Rating (1-5)</label>
                <input
                  style={s.input}
                  type="number"
                  min={1}
                  max={5}
                  value={t.rating}
                  onChange={(e) =>
                    setArrItem(
                      "testimonials",
                      i,
                      "rating",
                      Number(e.target.value),
                    )
                  }
                />
              </div>
            </div>
            <div style={s.inputGroup}>
              <label style={s.label}>Review Text</label>
              <textarea
                style={s.textarea}
                rows={3}
                value={t.text}
                onChange={(e) =>
                  setArrItem("testimonials", i, "text", e.target.value)
                }
              />
            </div>
          </div>
        ))}
        <button
          style={s.addItemBtn}
          onClick={() =>
            addArrItem("testimonials", {
              name: "",
              location: "",
              rating: 5,
              text: "",
            })
          }
        >
          <Plus size={16} /> Add Testimonial
        </button>
        <SaveBtn k="testimonials" />
      </Section>
    </div>
  );
};

const s = {
  container: {
    padding: "32px",
    backgroundColor: "#f9fafb",
    minHeight: "100vh",
  },
  header: { marginBottom: "28px" },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#111827",
    marginBottom: "6px",
  },
  pageSubtitle: { fontSize: "16px", color: "#6b7280" },
  section: {
    backgroundColor: "white",
    borderRadius: "12px",
    marginBottom: "16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  sectionHeader: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "18px 24px",
    background: "none",
    border: "none",
    cursor: "pointer",
    borderBottom: "1px solid #f3f4f6",
  },
  sectionTitle: { fontSize: "18px", fontWeight: "600", color: "#111827" },
  sectionBody: { padding: "24px" },
  formRow: { display: "flex", gap: "16px" },
  inputGroup: { marginBottom: "16px" },
  label: {
    display: "block",
    fontSize: "11px",
    fontWeight: "700",
    color: "#374151",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: "1.5px solid #e5e7eb",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Poppins', sans-serif",
    color: "#1f2937",
    backgroundColor: "#fff",
  },
  textarea: {
    width: "100%",
    padding: "10px 14px",
    border: "1.5px solid #e5e7eb",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    resize: "vertical",
    fontFamily: "'Poppins', sans-serif",
    boxSizing: "border-box",
    color: "#1f2937",
    backgroundColor: "#fff",
  },
  arrayCard: {
    backgroundColor: "#f9fafb",
    borderRadius: "10px",
    padding: "16px",
    marginBottom: "16px",
    border: "1px solid #e5e7eb",
  },
  arrayCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  arrayCardTitle: { fontSize: "14px", fontWeight: "600", color: "#374151" },
  removeBtn: {
    padding: "4px 8px",
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  addItemBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 20px",
    backgroundColor: "#f0fdf4",
    color: "#16a34a",
    border: "2px dashed #86efac",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    marginBottom: "20px",
    width: "100%",
    justifyContent: "center",
  },
  saveBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 24px",
    backgroundColor: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
  sizeHint: {
    margin: "6px 0 0",
    fontSize: "12px",
    color: "#6b7280",
    lineHeight: 1.5,
  },
};

export default ManageSiteContent;
